import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DocumentExt, ICoreAuthorizationDTO, IDocumentResponseDTO, MoveDocument, UploadDocument, UploadFile, UploadLetter } from '@app/core/model';
import { SharedDocumentsService } from '@app/shared/services/shared-documents.service';
import { ShellService } from '@mynexus/mfe-core';
import { Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DocumentsHHService extends SharedDocumentsService {

  constructor(
    http: HttpClient,
    shellService: ShellService,
  ) {
    super(http, shellService, 'HH');
  }

  override getFilesMetaData(tenantId: string, authorizationId: string, authorizationNumber: string): Observable<IDocumentResponseDTO[]> {
    return this.http.get<IDocumentResponseDTO[]>(`${this.API_URL}/Document/GetFilesMetaData/${tenantId}/${authorizationNumber}`);
  }

  override getFileById(tenantId: string, authorizationNumber: string, id: string): Observable<DocumentExt> {
    return this.http.get<DocumentExt>(`${this.API_URL}/Document/GetFile/${tenantId}/${authorizationNumber}/${id}`);
  }

  override updateDocumentCheck(tenantId: string, authorizationByProductId: string, docMetadataId: string, isUncheck = false) {
    return this.http.put<IDocumentResponseDTO>(
      `${this.API_URL}/${isUncheck ? 'Document/UncheckFile' : 'Document/CheckFile'}/${docMetadataId}`,
      null
    );
  }

  override moveFiles(tenantId: string, authorization: ICoreAuthorizationDTO, data: MoveDocument[]): Observable<boolean> {
    return this.http.post<boolean>(`${this.API_URL}/Document/MoveDocuments/${tenantId}/${authorization.authorizationNumber}`, data);
  }

  override getFilesByName(tenantId: string, authorizationNumber: string, documentFileName: string): Observable<IDocumentResponseDTO[]> {
    const encodedFileName = encodeURIComponent(documentFileName);
    return this.http.get<IDocumentResponseDTO[]>(`${this.API_URL}/Document/GetDocumentsByName/${tenantId}/${authorizationNumber}?documentFileName=${encodedFileName}`);
  }

 override uploadFile(tenantId: string, authorizationId: string, data: UploadFile[]): Observable<UploadFile[]> {
    data?.forEach(it => (it.isFromCreateAuth = false));
    const uploadedDocumentTypeList: string[] = data.flatMap(file => Array.isArray(file.documentTypes) ? file.documentTypes : []);
 
    return this.http.post<UploadFile[]>(`${this.API_URL}/Document/UploadDocuments`, data)
      .pipe(
        tap(files => {
          this.notifyFileUploadEvent(uploadedDocumentTypeList);
        })
      );
  }
 
  private notifyFileUploadEvent(uploadedDocumentTypeList: string[]): void {
    const event = new CustomEvent('hh-file-uploaded-event', {
      detail: {
        timestamp: Date.now(),
        uploadedDocumentTypeList: uploadedDocumentTypeList
      }
    });
    window.dispatchEvent(event);
  }

  override uploadLetter(tenantId: string, authorization: ICoreAuthorizationDTO, data: UploadLetter): Observable<UploadLetter> {
    return this.http.post<UploadLetter>(`${this.API_URL}/Document/UploadLetterTranslation/${authorization.authorizationNumber}`, data);
  }

  override mapAttachedFiles(files: UploadLetter[], config: {}): UploadLetter[] {
    return files.map(file => {
      return {
        authorizationDetailIds: file.lineIds,
        documentFileName: file.documentFileName,
        fileContent: file.fileContent,
        fileMetaData: file.fileMetaData,
        isUrgent: file.isUrgent,
        letterType: file.documentType,
        tenantId: config['tenantId'],
      } as UploadLetter;
    });
  }

  override deleteDocumentForMoveDocument(
    tenantId: string,
    authorizationByProductId: string,
    authorizationNumber: string,
    docMetaDataId: string,
    documentFileName: string,
  ): Observable<UploadDocument[]> {
    return this.http.delete<UploadDocument[]>(`${this.API_URL}/Document/DeleteDocument/${tenantId}/${authorizationNumber}/${docMetaDataId}`);
  }

  override updateDocumentReceivedTime(tenantId: string, authorizationByProductId: string, document: IDocumentResponseDTO): Observable<IDocumentResponseDTO> {
    return this.http.put<IDocumentResponseDTO>(`${this.API_URL}/Document/UpdateDocumentReceivedTime`, document);
  }

}
